/**
 * saas-auth `/api/reseller-admin/**` (shipped; bearer only, no cookies, no CSRF — H §5).
 * saas-auth publishes no OpenAPI file; wire types are hand-written from its DTOs
 * (`dto/saasadmin/*.java`, `dto/PagedResponse.java`) and normalized defensively at this boundary.
 *
 * Envelopes: every body is `{ success, message?, data }` — unwrapped by `unwrap()` — except
 * `GET /brand`, which is raw. Pages are `{ content, page, size, totalElements, totalPages }`
 * (0-based `page`). Errors `{ success:false, error:{ code, message } }` are normalized by the
 * factory (`details.code`).
 *
 * Per-tenant routes take the numeric saas-auth tenant id (`ResellerTenantView.id`), never the
 * slug. Unknown, unassigned and another reseller's tenant are all `404 RESOURCE_NOT_FOUND`.
 */
import { request } from './client';
import { ApiError } from './errors';
import {
  normalizeDomain,
  normalizeTemplate,
  normalizeVerify,
  type PutWhitelabelDomainRequest,
  type WhitelabelDomainDto,
  type WhitelabelTemplateDto,
  type WhitelabelVerifyResponse,
} from './saasAuthTypes';
import { asResellerRole, type ResellerRole } from '@/auth/authContextValue';

const BASE = '/api/reseller-admin';

/** saas-auth accepts at most this many `hosts` on a whitelabel PUT (SASATH-0342). */
export const MAX_WHITELABEL_HOSTS = 10;

/** Unwrap saas-auth's `{ success, data }` envelope when present; otherwise the body itself. */
export function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object') {
    const env = body as { success?: unknown; data?: unknown };
    if (env.success === true && 'data' in env) return env.data as T;
  }
  return body as T;
}

// ---------------------------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------------------------

/** One page of a saas-auth list (`PagedResponse`). */
export interface SaasPage<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface PageQuery {
  q?: string;
  page?: number;
  size?: number;
}

/** `ACTIVE | SUSPENDED` for a colleague. */
export type ColleagueStatus = 'ACTIVE' | 'SUSPENDED';

/** A colleague — `ResellerMemberDto`. `id` is the membership id (the `{memberId}` path variable). */
export interface Colleague {
  id: number;
  userId: number | null;
  email: string;
  name: string | null;
  role: ResellerRole;
  status: string;
  mfaEnabled: boolean;
  hasPassword: boolean;
  createdAt: string | null;
}

/** `PENDING | ACCEPTED | REVOKED | EXPIRED`, computed by saas-auth. */
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

export const INVITATION_STATUSES: readonly InvitationStatus[] = [
  'PENDING',
  'ACCEPTED',
  'REVOKED',
  'EXPIRED',
];

/** An invitation row — `ResellerInvitationDto` or `TenantInvitationDto` (the fields both carry). */
export interface Invitation {
  id: number;
  email: string;
  /** `OWNER | ADMIN | STAFF` (colleague) or `OWNER | ADMIN | STAFF | USER` (tenant); may be null. */
  role: string | null;
  status: string;
  createdAt: string | null;
  expiresAt: string | null;
}

/** A tenant membership row — `TenantMemberDto`. `membershipId` is the `{membershipId}` path variable. */
export interface TenantMember {
  membershipId: number;
  userId: number | null;
  name: string | null;
  email: string;
  /** Staff: `OWNER | ADMIN | STAFF`; customers: `USER` (claimed) or `GUEST` (roster only). */
  role: string;
  /** `ACTIVE | INVITED | DISABLED`. */
  status: string;
  createdAt: string | null;
}

/** Roles on a tenant: the staff table's three, and `USER` for a customer (`AddMemberRequest`). */
export type TenantRole = 'OWNER' | 'ADMIN' | 'STAFF' | 'USER';

/** A tenant membership status a reseller sets (`INVITED` is an invitation row, never set here). */
export type TenantMemberStatus = 'ACTIVE' | 'DISABLED';

/** The reseller's GHL connection — `ResellerGhlIntegrationDto`, token masked. */
export interface GhlIntegration {
  locationId: string | null;
  tokenSet: boolean;
  /** A short trailing fragment (`…wxyz`) — never the token. */
  tokenSuffix: string | null;
  active: boolean;
  updatedAt: string | null;
}

/**
 * `UpsertGhlIntegrationRequest`. A missing token keeps the stored one. A missing `active` makes
 * saas-auth default it to `true` — so an update of an existing connection sends its current state.
 */
export interface GhlIntegrationSave {
  locationId: string;
  privateIntegrationToken?: string;
  active?: boolean;
}

/** One classified GHL read — `GhlProbeOutcomeDto`. */
export interface GhlProbe {
  ok: boolean;
  status: number | null;
  kind: string | null;
  upstreamMessage: string | null;
}

/** `ResellerGhlIntegrationDiagnosisDto`. */
export interface GhlDiagnosis {
  locationId: string | null;
  credentialStructurallyPlausible: boolean;
  contacts: GhlProbe;
  customFields: GhlProbe;
  /** `HEALTHY | LIKELY_MISSING_SCOPE | LIKELY_CREDENTIAL_CORRUPT | LIKELY_CREDENTIAL_INVALID |
   *  UPSTREAM_UNAVAILABLE | INDETERMINATE` — shown as received. */
  interpretation: string;
}

// ---------------------------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizePage<T>(body: unknown, row: (raw: unknown) => T): SaasPage<T> {
  const raw = asRecord(unwrap(body));
  const content = Array.isArray(raw.content) ? raw.content.map(row) : [];
  return {
    content,
    page: num(raw.page) ?? 0,
    size: num(raw.size) ?? content.length,
    totalElements: num(raw.totalElements) ?? content.length,
    totalPages: num(raw.totalPages) ?? 1,
  };
}

function normalizeList<T>(body: unknown, row: (raw: unknown) => T): T[] {
  const raw = unwrap<unknown>(body);
  return Array.isArray(raw) ? raw.map(row) : [];
}

function normalizeColleague(value: unknown): Colleague {
  const raw = asRecord(value);
  return {
    id: num(raw.id) ?? 0,
    userId: num(raw.userId),
    email: str(raw.email) ?? '',
    name: str(raw.name),
    // An unknown role is the least privilege.
    role: asResellerRole(raw.role) ?? 'STAFF',
    status: str(raw.status) ?? '',
    mfaEnabled: raw.mfaEnabled === true,
    hasPassword: raw.hasPassword === true,
    createdAt: str(raw.createdAt),
  };
}

function normalizeInvitation(value: unknown): Invitation {
  const raw = asRecord(value);
  return {
    id: num(raw.id) ?? 0,
    email: str(raw.email) ?? '',
    role: str(raw.role),
    status: str(raw.status) ?? '',
    createdAt: str(raw.createdAt),
    expiresAt: str(raw.expiresAt),
  };
}

function normalizeMember(value: unknown): TenantMember {
  const raw = asRecord(value);
  return {
    membershipId: num(raw.membershipId) ?? 0,
    userId: num(raw.userId),
    name: str(raw.name),
    email: str(raw.email) ?? '',
    role: str(raw.role) ?? '',
    status: str(raw.status) ?? '',
    createdAt: str(raw.createdAt),
  };
}

function normalizeGhl(body: unknown): GhlIntegration {
  const raw = asRecord(unwrap(body));
  return {
    locationId: str(raw.locationId),
    tokenSet: raw.tokenSet === true,
    tokenSuffix: str(raw.tokenSuffix),
    active: raw.active !== false,
    updatedAt: str(raw.updatedAt),
  };
}

function normalizeProbe(value: unknown): GhlProbe {
  const raw = asRecord(value);
  return {
    ok: raw.ok === true,
    status: num(raw.status),
    kind: str(raw.kind),
    upstreamMessage: str(raw.upstreamMessage),
  };
}

function normalizeDiagnosis(body: unknown): GhlDiagnosis {
  const raw = asRecord(unwrap(body));
  return {
    locationId: str(raw.locationId),
    credentialStructurallyPlausible: raw.credentialStructurallyPlausible === true,
    contacts: normalizeProbe(raw.contacts),
    customFields: normalizeProbe(raw.customFields),
    interpretation: str(raw.interpretation) ?? '',
  };
}

// ---------------------------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------------------------

/** Query for a paged list — a blank `q` is omitted. */
function pageQuery(query: PageQuery): Record<string, string | number | undefined> {
  const q = query.q?.trim();
  return { q: q ? q : undefined, page: query.page, size: query.size };
}

function statusQuery(status: string | undefined): Record<string, string | undefined> | undefined {
  return status ? { status } : undefined;
}

function tenantBase(tenantId: number): string {
  return `${BASE}/tenants/${encodeURIComponent(String(tenantId))}`;
}

function withQuery(query: Record<string, string | number | undefined> | undefined) {
  return query ? { query } : {};
}

/** `404` — for the GHL connection, "none". */
function isMissing(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** Only an absolute https logo is rendered; anything else is dropped. */
function httpsUrl(value: unknown): string | null {
  const s = typeof value === 'string' && value.trim().length > 0 ? value : null;
  if (!s) return null;
  try {
    return new URL(s).protocol === 'https:' ? s : null;
  } catch {
    return null;
  }
}

/** The app-bar identity from `GET /api/reseller-admin/brand`. */
export interface ResellerBrand {
  displayName: string | null;
  logoUrl: string | null;
}

export const saasAuthResellerApi = {
  /**
   * The reseller's brand, else the product default. The body is the RAW `BrandResponse` (no
   * `{ success, data }` envelope — the one exception on this surface); only `displayName` and
   * `logoUrl` are read.
   */
  async brand(): Promise<ResellerBrand> {
    const body = await request<unknown>('GET', `${BASE}/brand`);
    const raw = asRecord(body);
    const displayName =
      typeof raw.displayName === 'string' && raw.displayName.trim().length > 0
        ? raw.displayName
        : null;
    return { displayName, logoUrl: httpsUrl(raw.logoUrl) };
  },

  // --- Colleagues (reseller-wide) -------------------------------------------------------------

  async listColleagues(query: PageQuery = {}): Promise<SaasPage<Colleague>> {
    const body = await request<unknown>('GET', `${BASE}/users`, { query: pageQuery(query) });
    return normalizePage(body, normalizeColleague);
  },

  /** Always `202` with no data (anti-enumeration) — nothing to return. */
  async inviteColleague(body: { email: string; role: ResellerRole }): Promise<void> {
    await request<unknown>('POST', `${BASE}/users`, { json: body });
  },

  async changeColleagueRole(memberId: number, role: ResellerRole): Promise<void> {
    await request<unknown>('PUT', `${BASE}/users/${memberId}/role`, { json: { role } });
  },

  async changeColleagueStatus(memberId: number, status: ColleagueStatus): Promise<void> {
    await request<unknown>('PUT', `${BASE}/users/${memberId}/status`, { json: { status } });
  },

  async removeColleague(memberId: number): Promise<void> {
    await request<unknown>('DELETE', `${BASE}/users/${memberId}`);
  },

  /** OWNER or ADMIN only on the server. Not paged. */
  async listColleagueInvitations(status?: string): Promise<Invitation[]> {
    const body = await request<unknown>(
      'GET',
      `${BASE}/invitations`,
      withQuery(statusQuery(status)),
    );
    return normalizeList(body, normalizeInvitation);
  },

  async resendColleagueInvitation(invitationId: number): Promise<void> {
    await request<unknown>('POST', `${BASE}/invitations/${invitationId}/resend`);
  },

  async revokeColleagueInvitation(invitationId: number): Promise<void> {
    await request<unknown>('DELETE', `${BASE}/invitations/${invitationId}`);
  },

  // --- GHL connection (OWNER only) ------------------------------------------------------------

  /** The connection, or `null` when there is none (`404`). */
  async getGhl(): Promise<GhlIntegration | null> {
    try {
      return normalizeGhl(await request<unknown>('GET', `${BASE}/ghl-integration`));
    } catch (err) {
      if (isMissing(err)) return null;
      throw err;
    }
  },

  /** A blank token is OMITTED from the body — saas-auth then keeps the stored one. */
  async saveGhl(input: GhlIntegrationSave): Promise<GhlIntegration> {
    const body: GhlIntegrationSave = { locationId: input.locationId.trim() };
    const token = input.privateIntegrationToken?.trim();
    if (token) body.privateIntegrationToken = token;
    if (input.active !== undefined) body.active = input.active;
    return normalizeGhl(await request<unknown>('PUT', `${BASE}/ghl-integration`, { json: body }));
  },

  async deleteGhl(): Promise<void> {
    await request<unknown>('DELETE', `${BASE}/ghl-integration`);
  },

  async diagnoseGhl(): Promise<GhlDiagnosis> {
    return normalizeDiagnosis(await request<unknown>('POST', `${BASE}/ghl-integration/diagnose`));
  },

  // --- Per tenant: people ---------------------------------------------------------------------

  /** The tenant's customers. */
  async listTenantUsers(tenantId: number, query: PageQuery = {}): Promise<SaasPage<TenantMember>> {
    const body = await request<unknown>('GET', `${tenantBase(tenantId)}/users`, {
      query: pageQuery(query),
    });
    return normalizePage(body, normalizeMember);
  },

  async listTenantStaff(tenantId: number, query: PageQuery = {}): Promise<SaasPage<TenantMember>> {
    const body = await request<unknown>('GET', `${tenantBase(tenantId)}/staff`, {
      query: pageQuery(query),
    });
    return normalizePage(body, normalizeMember);
  },

  /** Always `202` with the pending invitation — never a direct add. `appId` is not sent. */
  async inviteTenantUser(
    tenantId: number,
    body: { email: string; role: TenantRole },
  ): Promise<void> {
    await request<unknown>('POST', `${tenantBase(tenantId)}/users`, { json: body });
  },

  async changeTenantMemberRole(
    tenantId: number,
    membershipId: number,
    role: TenantRole,
  ): Promise<void> {
    await request<unknown>('PUT', `${tenantBase(tenantId)}/users/${membershipId}/role`, {
      json: { role },
    });
  },

  async changeTenantMemberStatus(
    tenantId: number,
    membershipId: number,
    status: TenantMemberStatus,
  ): Promise<void> {
    await request<unknown>('PUT', `${tenantBase(tenantId)}/users/${membershipId}/status`, {
      json: { status },
    });
  },

  async removeTenantMember(tenantId: number, membershipId: number): Promise<void> {
    await request<unknown>('DELETE', `${tenantBase(tenantId)}/users/${membershipId}`);
  },

  async listTenantInvitations(tenantId: number, status?: string): Promise<Invitation[]> {
    const body = await request<unknown>(
      'GET',
      `${tenantBase(tenantId)}/invitations`,
      withQuery(statusQuery(status)),
    );
    return normalizeList(body, normalizeInvitation);
  },

  async resendTenantInvitation(tenantId: number, invitationId: number): Promise<void> {
    await request<unknown>('POST', `${tenantBase(tenantId)}/invitations/${invitationId}/resend`);
  },

  async revokeTenantInvitation(tenantId: number, invitationId: number): Promise<void> {
    await request<unknown>('DELETE', `${tenantBase(tenantId)}/invitations/${invitationId}`);
  },

  // --- Per tenant: white-label domain ---------------------------------------------------------

  /** Not configured is `{ configured: false }` — a `404` means the tenant is not this reseller's. */
  async getDomain(tenantId: number): Promise<WhitelabelDomainDto> {
    const body = await request<unknown>('GET', `${tenantBase(tenantId)}/whitelabel-domain`);
    return normalizeDomain(unwrap(body));
  },

  async getDomainTemplate(tenantId: number): Promise<WhitelabelTemplateDto> {
    const body = await request<unknown>(
      'GET',
      `${tenantBase(tenantId)}/whitelabel-domain/template`,
    );
    return normalizeTemplate(unwrap(body));
  },

  async putDomain(
    tenantId: number,
    body: PutWhitelabelDomainRequest,
  ): Promise<WhitelabelDomainDto> {
    const raw = await request<unknown>('PUT', `${tenantBase(tenantId)}/whitelabel-domain`, {
      json: body,
    });
    return normalizeDomain(unwrap(raw));
  },

  async removeDomain(tenantId: number): Promise<void> {
    await request<unknown>('DELETE', `${tenantBase(tenantId)}/whitelabel-domain`);
  },

  async verifyDomain(tenantId: number): Promise<WhitelabelVerifyResponse> {
    const raw = await request<unknown>('POST', `${tenantBase(tenantId)}/whitelabel-domain/verify`);
    return normalizeVerify(unwrap(raw));
  },
};
